import { BenefitAdministrator } from '@bariendo/lib/constants';
import { useDebouncedValue } from '@mantine/hooks';
import { MedplumClient } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import { fetchBot } from '@/services/services';
import type {
  CoverageLookupEvent,
  EmployerBenefitResult,
  EmployerSearchResult,
} from '../../../bots/src/handlers/coverage-lookup';

const COVERAGE_LOOKUP_BOT = 'coverage-lookup';
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 8;

function lookUp<T>(medplum: MedplumClient, input: CoverageLookupEvent): Promise<T> {
  return fetchBot<T>(medplum, COVERAGE_LOOKUP_BOT, input);
}

/**
 * Who administers the employer's benefit, through `coverage-lookup` (BAR-2918): Carrum,
 * Transcarent, or null when no partner covers it. Throws when the check fails, so an outage
 * never reads as "no benefit".
 */
export async function checkEmployerBenefit(
  medplum: MedplumClient,
  employer: string
): Promise<BenefitAdministrator | null> {
  const { administrator } = await lookUp<EmployerBenefitResult>(medplum, { action: 'check', employer });
  return administrator;
}

/**
 * The employer directory typeahead (BAR-2918, BAR-3077): matches for `query`, debounced, with a
 * reply that arrives after a newer query dropped. Searches nothing while `skip` is set, or for
 * `current`, the name already chosen.
 */
export function useEmployerSearch(
  query: string,
  options: { skip?: boolean; current?: string } = {}
): { options: string[]; searching: boolean; searchFailed: boolean } {
  const { skip = false, current } = options;
  const medplum = useMedplum();
  const [debouncedQuery] = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  const [matches, setMatches] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);

  useEffect(() => {
    const term = debouncedQuery.trim();
    if (skip || !term || term === current) {
      setMatches((existing) => (existing.length > 0 ? [] : existing));
      setSearching(false);
      setSearchFailed(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    setSearchFailed(false);
    lookUp<EmployerSearchResult>(medplum, { action: 'search', query: term, limit: SEARCH_LIMIT })
      .then(({ employers }) => {
        if (!cancelled) setMatches(employers);
      })
      .catch(() => {
        if (!cancelled) {
          setMatches([]);
          setSearchFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, current, skip, medplum]);

  return { options: matches, searching, searchFailed };
}
